/**
 * Início (DEMO-03, telas/Inicio.tsx), na ordem aprovada:
 *  1. Precisa da sua atenção (as ações mais urgentes dos módulos já disponíveis);
 *  2. Oportunidades por estágio (com acesso ao Kanban);
 *  3. Meta comercial (só quando a meta estiver configurada em Parâmetros);
 *  4. Resumos por módulo.
 * Módulos: Comercial, Operações (Ordens em conferência; Pedidos com OC do Cliente pendente), Administração e, no
 * Pacote 5, o Financeiro (consultas próprias, somente leitura; cada categoria com a permissão da sua fonte; um perfil
 * só do Financeiro também vê o Início). F1-45: sem textos desatualizados ("sem proposta", "chega com…").
 */
import { usePersonalizacao } from './personalizacao.tsx';
import { useState } from 'react';
import { api } from '../api.ts';
import { useSessao } from '../sessao.tsx';
import { dataBr, type NivelRegistro } from '../../../backend/src/compartilhado/oportunidades.ts';
import { AlertaPrioridade } from './filtros.tsx';
import { EstadoCarga, ResumoIndicadores, useCarga } from './ui.tsx';
import { moeda } from './tiposOportunidade.ts';

interface ItemAtencao {
  chave: string;
  modulo: 'Comercial' | 'Operações' | 'Administração' | 'Financeiro';
  detalhe?: string | null;
  valor?: string | null;
  categoria?: number;
  acao: string;
  relacionado: { texto: string; rota: string };
  responsavel: string;
  vencimento: string;
  nivel: NivelRegistro;
  rota: string;
}
interface DadosInicio {
  hoje: string;
  atencao: ItemAtencao[];
  estagios: { id: string; nome: string; quantidade: number }[];
  abertas: number;
  meta: { valor: number; vigente_de: string; vigente_ate: string | null } | null;
}

interface DadosFinanceiro {
  hoje: string;
  prazoFechamentoDia: number;
  categorias: { id: number; codigo: string; rotulo: string; critica: boolean; unidade: string; quantidade: number; criticas: number }[];
  itens: ItemAtencao[];
}
/** O Início abre para quem vê o Comercial OU alguma fonte do Financeiro (Pagar, Receber, Conciliação). */
export const veFinanceiroNoInicio = (pode: (acao: string, entidade: string) => boolean) => pode('visualizar', 'conta_pagar') || pode('visualizar', 'conta_receber') || pode('visualizar', 'conciliacao');

export function Inicio() {
  const { pode } = useSessao();
  const [todas, setTodas] = useState(false);
  const comercial = pode('visualizar', 'oportunidade');
  const financeiro = veFinanceiroNoInicio(pode);
  const carga = useCarga(async () => {
    const [c, f] = await Promise.all([comercial ? api.get<DadosInicio>('/api/inicio') : Promise.resolve(null), financeiro ? api.get<DadosFinanceiro>('/api/inicio/financeiro') : Promise.resolve(null)]);
    return { c, f };
  }, []);
  const pers = usePersonalizacao('inicio-atencao'); // D7: colunas e visões de "Precisa da sua atenção"
  if (!carga.dados) return <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} teste="inicio" />;
  const fin = carga.dados.f;
  const { hoje, atencao, estagios, abertas, meta } = carga.dados.c ?? { hoje: fin!.hoje, atencao: [], estagios: [], abertas: 0, meta: null };
  // o Financeiro entra na mesma lista; as ações já vêm na ordem (crítica mais antiga → maior valor → vencimento)
  const PESO: Record<string, number> = { critico: 0, urgente: 1, atencao: 2, aviso: 3, valido: 4, substituido: 5 };
  const urgentes = [...atencao, ...(fin?.itens ?? [])].filter((i) => i.nivel !== 'valido').sort((a, b) => PESO[a.nivel] - PESO[b.nivel] || a.vencimento.localeCompare(b.vencimento));
  const porModulo = (mod: ItemAtencao['modulo']) => urgentes.filter((i) => i.modulo === mod);
  const cartao = (mod: ItemAtencao['modulo'], rota: string) => {
    const l = mod === 'Financeiro' ? (fin?.itens ?? []) : porModulo(mod);
    return (
      <section key={mod} className="cartao cartao-modulo" aria-labelledby={`t-mod-${mod}`} data-teste={`modulo-${mod}`}>
        <h3 id={`t-mod-${mod}`}>{mod}</h3>
        <ResumoIndicadores compacto itens={[{ rotulo: 'Ações', valor: l.length }, { rotulo: 'Críticas', valor: l.filter((i) => i.nivel === 'critico').length }]} />
        <p className="proxima-modulo">
          <span className="texto-secundario">Principal próxima ação: </span>
          {l[0] ? <a href={`#${l[0].rota}`}>{l[0].acao}</a> : 'nenhuma pendência'}
        </p>
        <a className="botao-secundario pequeno" href={`#${rota}`}>
          Abrir {mod}
        </a>
      </section>
    );
  };
  return (
    <div className="inicio">
      <section className="cartao atencao" aria-labelledby="t-atencao" data-teste="proximas-acoes">
        <div className="barra-titulo">
          <h1 id="t-atencao" className="titulo-pagina">
            Precisa da sua atenção
          </h1>
          <span className="texto-secundario">
            {urgentes.length} ação(ões) · {urgentes.filter((i) => i.nivel === 'critico').length} crítica(s)
          </span>
          <div className="acoes-direita">
            {pers.controles}
            {comercial && (
              <a className="botao-secundario pequeno" href="#/tarefas?situacao=abertas">
                Ver todas as tarefas
              </a>
            )}
          </div>
        </div>
        {urgentes.length === 0 ? (
          <p className="vazio" data-teste="atencao-vazia">
            Nenhuma ação pendente.
          </p>
        ) : (
          <div className="rolagem">
            <table ref={pers.tabela} className="tabela tabela-compacta tabela-atencao" data-teste="tabela-atencao">
              <thead>
                <tr>
                  <th>Prioridade</th>
                  <th>Módulo</th>
                  <th>Ação</th>
                  <th>Cliente / registro</th>
                  <th>Responsável</th>
                  <th>Vencimento</th>
                </tr>
              </thead>
              <tbody>
                {(todas ? urgentes : urgentes.slice(0, 10)).map((i) => (
                  <tr key={i.chave} className={`prioridade-${i.nivel}`} data-teste={`atencao-${i.chave}`} data-modulo={i.modulo}>
                    <td>
                      <AlertaPrioridade nivel={i.nivel} />
                    </td>
                    <td>{i.modulo}</td>
                    <td>
                      <a href={`#${i.rota}`}>{i.acao}</a>
                      {i.detalhe && <span className="texto-secundario"> · {i.detalhe}</span>}
                    </td>
                    <td>
                      <a href={`#${i.relacionado.rota}`}>{i.relacionado.texto}</a>
                    </td>
                    <td>{i.responsavel}</td>
                    <td className="nowrap">{i.vencimento < hoje ? `venceu em ${dataBr(i.vencimento)}` : i.vencimento === hoje ? 'hoje' : dataBr(i.vencimento)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {urgentes.length > 10 && (
          <p className="texto-secundario">
            {todas ? `Mostrando todas as ${urgentes.length} ações.` : `Mostrando as 10 mais urgentes de ${urgentes.length}.`}{' '}
            <button type="button" className="botao-link" onClick={() => setTodas(!todas)} aria-expanded={todas} data-teste="mostrar-todas-atencao">
              {todas ? 'Mostrar só as 10 mais urgentes' : `Mostrar todas (${urgentes.length})`}
            </button>
          </p>
        )}
      </section>

      {comercial && (
      <div className="grade-inicio-meio">
        <section className="cartao" aria-labelledby="t-estagios" data-teste="estagios">
          <div className="barra-titulo">
            <h2 id="t-estagios">Oportunidades por estágio</h2>
            <div className="acoes-direita">
              <a className="botao-secundario pequeno" href="#/oportunidades">
                Abrir o Kanban
              </a>
            </div>
          </div>
          <ResumoIndicadores teste="resumo-estagios" itens={estagios.map((e) => ({ rotulo: e.nome, valor: e.quantidade }))} />
          <p className="texto-secundario sem-margem">{abertas} oportunidade(s) em aberto</p>
        </section>

        {meta && (
          <section className="cartao cartao-metas" aria-labelledby="t-metas" data-teste="cartao-metas">
            <h2 id="t-metas">Meta comercial</h2>
            <ResumoIndicadores compacto itens={[{ rotulo: 'Meta do período', valor: moeda(meta.valor), detalhe: `${dataBr(meta.vigente_de)} a ${meta.vigente_ate ? dataBr(meta.vigente_ate) : 'sem fim definido'}` }]} />
          </section>
        )}
      </div>
      )}

      <h2 className="titulo-modulos">Resumo por módulo</h2>
      <div className="grade-3 cartoes-modulos" data-teste="cartoes-modulos">
        {comercial && cartao('Comercial', '/oportunidades')}
        {comercial && (pode('visualizar', 'pedido') || pode('visualizar', 'ordem')) && cartao('Operações', pode('visualizar', 'ordem') ? '/ordens' : '/pedidos')}
        {fin && cartao('Financeiro', pode('visualizar', 'conta_receber') ? '/contas-receber' : pode('visualizar', 'conta_pagar') ? '/contas-pagar' : '/conciliacao')}
        {comercial && cartao('Administração', '/administracao/parceiros')}
      </div>
      {fin && (
        <section className="cartao" aria-labelledby="t-fin-categorias" data-teste="financeiro-categorias">
          <h2 id="t-fin-categorias">Financeiro · categorias</h2>
          <p className="texto-secundario sem-margem">Cada pendência entra em uma só categoria, a primeira que se aplica. Comissão com várias parcelas conta uma vez. Mês aberto vira ação crítica só depois do dia {fin.prazoFechamentoDia} do mês seguinte.</p>
          <div className="rolagem">
            <table className="tabela tabela-compacta" data-teste="tabela-financeiro-categorias">
              <thead>
                <tr>
                  <th>Categoria</th>
                  <th className="numero">Qtd</th>
                  <th>Crítica</th>
                  <th>Unidade</th>
                </tr>
              </thead>
              <tbody>
                {fin.categorias.map((k) => (
                  <tr key={k.id} data-teste={`fin-categoria-${k.codigo}`}>
                    <td>{k.rotulo}</td>
                    <td className="numero">{k.quantidade}</td>
                    <td>{k.codigo === 'fechamento' ? (k.criticas ? `sim (${k.criticas})` : 'só depois do prazo') : k.critica ? 'sim' : 'não'}</td>
                    <td>{k.unidade}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
